import { useState } from 'react';
import type React from 'react';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IconButton, cx } from '@/components/ui';
import { notificationService } from '@/services/notificationService';

export interface GameLoginProps {
  username?: string | null;
  password?: string | null;
  /** `rows`: label above value (the game sheet); `inline`: one wrapping line (My games cards). Buttons are 48 design px in both. */
  layout?: 'rows' | 'inline';
  className?: string;
}

const HIDDEN_PASSWORD = '••••••';
const EMPTY = '—';

/**
 * A game account's login: username and password (hidden until shown), each with a copy button.
 * Taps on its buttons stay here, so it can sit inside a tappable card.
 */
export default function GameLogin({ username, password, layout = 'rows', className }: GameLoginProps) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const rows = layout === 'rows';

  const copy = (value: string | null | undefined, copiedMessage: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!value) return;
    navigator.clipboard?.writeText(value).then(
      () => notificationService.success(copiedMessage),
      () => undefined
    );
  };

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShow((v) => !v);
  };

  const copyUsername = (
    <IconButton
      size="xl"
      variant="ghost"
      label={t('games.login.copyUsername')}
      icon={<Copy />}
      disabled={!username}
      onClick={copy(username, t('games.login.usernameCopied'))}
    />
  );
  const showHide = (
    <IconButton
      size="xl"
      variant="ghost"
      label={show ? t('games.hidePassword') : t('games.showPassword')}
      icon={show ? <EyeOff /> : <Eye />}
      onClick={toggle}
    />
  );
  const copyPassword = (
    <IconButton
      size="xl"
      variant="ghost"
      label={t('games.login.copyPassword')}
      icon={<Copy />}
      disabled={!password}
      onClick={copy(password, t('games.login.passwordCopied'))}
    />
  );
  const passwordText = show ? password || EMPTY : HIDDEN_PASSWORD;

  if (!rows) {
    return (
      <div
        role="group"
        aria-label={t('games.login.title')}
        className={cx('flex min-w-0 flex-wrap items-center gap-x-3 font-ui text-small text-tertiary', className)}
      >
        <span className="inline-flex min-w-0 items-center gap-0.5">
          <span className="min-w-0 break-words">
            {t('games.login.username')}: <span className="text-secondary">{username || EMPTY}</span>
          </span>
          {copyUsername}
        </span>
        <span className="inline-flex min-w-0 items-center gap-0.5">
          <span className="min-w-0 break-words">
            {t('games.login.password')}: <span className="text-secondary">{passwordText}</span>
          </span>
          {showHide}
          {copyPassword}
        </span>
      </div>
    );
  }

  return (
    <div role="group" aria-label={t('games.login.title')} className={cx('flex min-w-0 flex-col gap-2', className)}>
      <div className="flex min-w-0 items-center gap-1 rounded-control border border-edge-subtle bg-inset py-1 pl-3 pr-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="font-ui text-small text-tertiary">{t('games.login.username')}</span>
          <span className="break-all font-money text-body text-primary">{username || EMPTY}</span>
        </div>
        {copyUsername}
      </div>
      <div className="flex min-w-0 items-center gap-1 rounded-control border border-edge-subtle bg-inset py-1 pl-3 pr-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="font-ui text-small text-tertiary">{t('games.login.password')}</span>
          <span className="break-all font-money text-body text-primary">{passwordText}</span>
        </div>
        {showHide}
        {copyPassword}
      </div>
    </div>
  );
}
